import { JsonArray } from '@eagle/common';
import { AlertResponse, Person, Thing } from '@eagle/core-data-types';
import { ListItemButton, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';
import { CacheDataTypes } from '../../types/cache';
import { testid } from '../../util/test-id';
import { AlertLabel } from '../alert-label';
import { FetchOne } from '../fetch';
import { FormatAddress } from '../format/format-address';
import { FormatTimestamp } from '../format/format-timestamp';
import { GroupChipElement } from '../group-chip-element';
import { Icon } from '../icons';

const AlertItemButton = styled(ListItemButton)({
  color: 'inherit',
  cursor: 'pointer',
  padding: '8px',
  textDecoration: 'none',
  width: '100%',
});

interface AlertDetailsProps {
  address: JsonArray;
  alert: AlertResponse;
  showPerson: boolean;
  showThing: boolean;
}

interface FormatMultipleDisplayProps {
  'data-testid'?: string;
  ids: string[];
  renderer: (id: string) => JSX.Element;
}

const FormatMultipleDisplay: FC<FormatMultipleDisplayProps> = ({ ids, renderer, ...props }) => {
  const { t } = useTranslation(['common']);
  if (!ids.length) return <></>;

  return (
    <Stack data-testid={props['data-testid']} direction="column">
      <span>{renderer(ids[0])}</span>
      <Typography mt={-1} variant="caption">
        {ids.length > 1
          && <span>{t('common:common.labels.othersWithCount', { count: ids.length - 1 })}</span>
        }
      </Typography>
    </Stack>
  );
};

export const AlertListItemMobile: FC<AlertDetailsProps> = ({ alert, address, showPerson, showThing }): JSX.Element => {
  const { t } = useTranslation(['common']);
  const href = useHref('/alert');
  const alertHref = `${href}/${alert.alertId}`;

  return (
    <AlertItemButton
      role="button"
      onClick={() => { window.location.assign(alertHref); }}
    >
      <Stack spacing={1} width={'100%'} data-testid={testid`alert-id-${alert.data.testId ?? alert.alertId}`}>
        <Stack direction="row" gap={2} display="flex" justifyContent="flex-start">
          <Stack gap={1} flex={[3, 3]}>
            {showThing &&
              <Stack direction="row" spacing={0.5}>
                <Icon icon="ThingIcon" style={{ fontSize: 20 }} />
                {!alert.thingIds.length
                  ? <Typography data-testid="thing-no-results" variant="body2" fontStyle="italic" color="text.secondary">
                    {t('common:component.alert-table.hint.no-results.none')}
                  </Typography>
                  : <FormatMultipleDisplay
                    ids={alert.thingIds}
                    data-testid="thing-display"
                    renderer={(id: string) => (
                      <FetchOne
                        dataType={CacheDataTypes.THING}
                        id={id}
                        renderFactory={(thing: Thing) => (
                          <Typography variant="body2">
                            {thing.display}
                          </Typography>
                        )}
                      />
                    )}
                  />
                }
              </Stack>
            }
            {showPerson
              && <Stack direction="row" spacing={0.5}>
                <Icon icon="PersonIcon" style={{ fontSize: 20 }} />
                {!alert.personIds.length
                  ? <Typography data-testid="person-no-results" variant="body2" fontStyle="italic" color="text.secondary">
                    {t('common:component.alert-table.hint.no-results.none')}
                  </Typography>
                  : <FormatMultipleDisplay
                    ids={alert.personIds}
                    data-testid="person-display"
                    renderer={(id: string) => (
                      <FetchOne
                        dataType={CacheDataTypes.PERSON}
                        id={id}
                        renderFactory={(person: Person) => (
                          <Typography variant="body2">
                            {person.display}
                          </Typography>
                        )}
                      />
                    )}
                  />
                }
              </Stack>
            }
          </Stack>
          <Stack data-chromatic="ignore" flex={[2, 2]} alignItems="flex-end">
            <Typography data-testid="relative-time" variant="body2" data-chromatic="ignore"><FormatTimestamp value={alert.occurred} format="relative" /></Typography>
            <Typography data-chromatic="ignore" data-testid="date-time" color="text.secondary" variant="caption" textAlign="right" whiteSpace="nowrap">
              <FormatTimestamp value={alert.occurred} format="" />
            </Typography>
          </Stack>
        </Stack>
        <Stack my={1} direction="row" spacing={0.5}>
          <Icon icon="LocationIcon" style={{ fontSize: 20 }} />
          {!address.length
            ? <Typography variant="body2" fontStyle="italic" color="text.secondary">
              {t('common:component.alert-table.hint.no-results.address')}
            </Typography>
            : <Stack>
              <FormatAddress value={address as string[]} variant="body2" />
            </Stack>
          }
        </Stack>
        <Stack>
          <AlertLabel align='center' alert={alert} />
        </Stack>
        <Stack direction="row" width={1} pt={1} flexWrap="wrap">
          {!alert.groupIds || !alert.groupIds.length
            ? <Typography color="text.secondary" fontStyle="italic" variant="body2">
              {t('common:component.alert-table.hint.no-results.groups')}
            </Typography>
            : <GroupChipElement groupIds={alert.groupIds} />
          }
        </Stack>
      </Stack>
    </AlertItemButton>
  );
};
